import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslate } from '@tolgee/react';
import { ALL, UPLOAD_TYPE_POST_MEDIA } from '../../../../config/constants';
import type { OverlayTemplateDetailDto } from '../../../../api/overlayTemplates';
import {
  LomavisTemplateEditor,
  type TemplateEditorSavePayload
} from '../../../../lomavisSharedUi/templates/editors/LomavisTemplateEditor';
import {
  normalizeTemplate,
  type TemplateDefinition,
  type TemplateLayer
} from '../../../../lomavisSharedUi/templates/editors/LomavisTemplateEditor/templateDefinition';
import {
  handleUploadMedia,
  getImageDimensionsFromfile
} from '../../utils/helpers';
import {
  deleteAfile,
  updateMediaAssetFile
} from '../../../../redux/slices/lomavisCreator/lomavisCreator';
import { lomavisCreatorRootState } from '../../../../pages/lomavisCreator';
import ModalBody from '../../../modals/modalBody';
import toast from 'react-hot-toast';
import CarouselImageCard from '../../cards/carouselImageCard';
import { ConfirmationModal } from '../confirmationModal';
import axiosClient from '../../../../utils/axios';
import { relocatePublishButtons } from '../../utils/uploadMediaFile';
import OverlayTemplateSelectorModal from './OverlayTemplateSelectorModal';
import { Button } from 'react-bootstrap';
import { OVERLAY_TEMPLATE_CREATOR_BEHAVIOUR } from '../../config/overlayTemplateCreator';

type Props = {
  template: OverlayTemplateDetailDto;
  media: any[];
  platform?: string;
  onTemplateChange: (template: OverlayTemplateDetailDto) => void;
  onComplete: () => void;
};

const templateForMedia = (
  template: OverlayTemplateDetailDto,
  media: any
): TemplateDefinition => {
  const definition = normalizeTemplate(template.configuration);
  const background = definition.layers.find(
    (layer) => layer.kind === 'background'
  );
  const image = {
    src: media.original_asset || media.preview_image,
    uuid: media.uuid,
    fit: 'cover' as const
  };
  const backgroundLayer: TemplateLayer = background
    ? { ...background, image: { ...background.image, ...image } }
    : {
        id: 'template-media-background',
        name: 'Background image',
        kind: 'background',
        locked: false,
        visible: true,
        position: { x: 0, y: 0 },
        size: {
          width: definition.canvas.width,
          height: definition.canvas.height
        },
        image
      };

  return {
    ...definition,
    name: template.title,
    layers: [
      backgroundLayer,
      ...definition.layers.filter((layer) => layer.kind !== 'background')
    ]
  };
};

const OverlayTemplateImageEditor: React.FC<Props> = ({
  template,
  media,
  platform = ALL,
  onTemplateChange,
  onComplete
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslate();
  const locationUuid = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator.locationUuid
  );
  const [currentIndex, setCurrentIndex] = useState(0);
  const currentMedia = media[currentIndex];
  const [activeTemplate, setActiveTemplate] = useState(template);
  const [value, setValue] = useState<TemplateDefinition>(() =>
    templateForMedia(template, media[0])
  );
  const [saving, setSaving] = useState(false);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [pendingTemplate, setPendingTemplate] =
    useState<OverlayTemplateDetailDto | null>(null);
  const [showTemplateChangeConfirmation, setShowTemplateChangeConfirmation] =
    useState(false);
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [rightMargin, setRightMargin] = useState(0);
  const footerRef = useRef<HTMLElement | null>(null);
  const pristineValueRef = useRef(value);
  const [savedPreviewUrls, setSavedPreviewUrls] = useState<
    Record<string, string>
  >({});
  const creatorMedia = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.postData[platform]?.media || []
  );

  useEffect(() => {
    const nextValue = templateForMedia(activeTemplate, currentMedia);
    setValue(nextValue);
    pristineValueRef.current = nextValue;
  }, [activeTemplate, currentMedia]);

  useEffect(() => {
    if (template.uuid === activeTemplate.uuid) return;
    setActiveTemplate(template);
  }, [activeTemplate.uuid, template]);

  useEffect(() => {
    const relocate = () =>
      relocatePublishButtons({ footerRef, setRightMargin });
    const timeout = window.setTimeout(relocate, 300);
    window.addEventListener('resize', relocate);
    return () => {
      window.clearTimeout(timeout);
      window.removeEventListener('resize', relocate);
    };
  }, []);

  const configuration = useMemo(
    () => JSON.parse(JSON.stringify(value)) as Record<string, unknown>,
    [value]
  );

  const hasUnsavedTemplateEdits =
    JSON.stringify(value) !== JSON.stringify(pristineValueRef.current);

  const applyTemplateChange = (nextTemplate: OverlayTemplateDetailDto) => {
    setActiveTemplate(nextTemplate);
    onTemplateChange(nextTemplate);
    setPendingTemplate(null);
    setShowTemplateChangeConfirmation(false);
  };

  const save = async (payload: TemplateEditorSavePayload) => {
    if (payload.format !== 'png' || !currentMedia) return;
    setSaving(true);
    try {
      const dimensions = await getImageDimensionsFromfile(payload.file);
      const uploadPreset = await handleUploadMedia({
        sourceFile: payload.file,
        dimensions,
        location_uuid: locationUuid,
        uploadType: UPLOAD_TYPE_POST_MEDIA,
        t
      });
      if (!uploadPreset?.uuid)
        throw new Error('Rendered template upload failed');

      dispatch(
        updateMediaAssetFile({
          uuid: currentMedia.uuid,
          preview_image: uploadPreset.uploaded_object_data?.preview_image_url,
          file: payload.file,
          assetBlob: payload.file,
          platform,
          width: dimensions?.width,
          height: dimensions?.height,
          uploadPresets: uploadPreset,
          source_media_uuid: currentMedia.uuid,
          overlayTemplateUuid: activeTemplate.uuid,
          overlayTemplateConfiguration: configuration
        })
      );
      const renderedPreviewUrl =
        uploadPreset.uploaded_object_data?.preview_image_url ||
        uploadPreset.uploaded_object_data?.original_asset;
      if (renderedPreviewUrl) {
        setSavedPreviewUrls((urls) => ({
          ...urls,
          [currentMedia.uuid]: renderedPreviewUrl
        }));
      }
      toast.success(
        t(
          'lomavisCreator.templateEditor.editSaved',
          'Template image saved.'
        ),
        { position: 'top-right' }
      );

      if (currentIndex + 1 < media.length) {
        setCurrentIndex((index) => index + 1);
      } else {
        onComplete();
      }
    } finally {
      setSaving(false);
    }
  };

  const discardChanges = async () => {
    setDiscarding(true);
    const editorMedia = media.map((sessionMedia) => ({
      sessionMedia,
      creatorMedia:
        creatorMedia.find(
          (creatorItem) =>
            creatorItem.uuid === sessionMedia.uuid ||
            creatorItem.source_media_uuid === sessionMedia.uuid
        ) || sessionMedia
    }));
    const unsavedEditorMedia = editorMedia.filter(
      ({ creatorMedia: currentCreatorMedia }) =>
        currentCreatorMedia.editorUpdateState === true
    );

    try {
      const deleteUrls = Array.from(
        new Set(
          unsavedEditorMedia
            .flatMap(({ sessionMedia, creatorMedia: currentCreatorMedia }) => {
              const sourceUrl = sessionMedia.saved_in_media_library
                ? null
                : sessionMedia.api_url ||
                  sessionMedia.uploadPreset?.uploaded_object_data?.api_url;
              const renderedUrl =
                currentCreatorMedia.uuid === sessionMedia.uuid
                  ? null
                  : currentCreatorMedia.api_url ||
                    currentCreatorMedia.uploadPreset?.uploaded_object_data
                      ?.api_url;
              return [sourceUrl, renderedUrl];
            })
            .filter(Boolean)
        )
      );
      const results = await Promise.allSettled(
        deleteUrls.map(async (url) => await axiosClient.delete(url))
      );
      if (results.some((result) => result.status === 'rejected')) {
        toast.error(
          t(
            'lomavisCreator.templateEditor.discardDeleteFailed',
            'Some temporary media could not be deleted from the server.'
          ),
          { position: 'top-right' }
        );
      }

      unsavedEditorMedia.forEach(({ creatorMedia: item }) => {
        dispatch(
          deleteAfile({
            platform,
            fileID: item.id || item.uuid,
            uuid: item.uuid
          })
        );
      });
      setShowExitConfirmation(false);
      onComplete();
    } finally {
      setDiscarding(false);
    }
  };

  return (
    <ModalBody
      wide
      onClose={() => setShowExitConfirmation(true)}
    >
      <div className="d-flex h-100 w-100 gap-3 overflow-hidden">
        {media.length > 1 && (
          <aside
            className="bg-white p-2 rounded-3 overflow-auto"
            style={{ flex: '0 0 140px', width: 140 }}
          >
            {media.map((item, index) => (
            <div
              key={item.uuid}
              className="w-100 mb-2 d-flex justify-content-center"
            >
                <CarouselImageCard
                  active={index === currentIndex}
                  image={
                    savedPreviewUrls[item.uuid] ||
                    item.original_asset ||
                    item.preview_image
                  }
                  item={item}
                />
            </div>
            ))}
          </aside>
        )}
        <main className="flex-grow-1 overflow-hidden">
          <LomavisTemplateEditor
            value={value}
            onChange={setValue}
            saveFormat="png"
            showTemplateName={false}
            quickTextEditing
            headerPosition="bottom"
            readOnlyCanvasSize
            headerRef={footerRef}
            headerActionsStyle={{ marginRight: `${rightMargin}px` }}
            headerActions={
              OVERLAY_TEMPLATE_CREATOR_BEHAVIOUR.allowTemplateChange && (
              <Button
                type="button"
                size="sm"
                variant="light"
                disabled={saving}
                onClick={() => setShowTemplateSelector(true)}
              >
                {t(
                  'lomavisCreator.templateEditor.changeTemplate',
                  'Change template'
                )}
              </Button>
              )
            }
            isSaving={saving}
            onSave={(payload) => {
              save(payload).catch(() => {
                toast.error(
                  t(
                    'lomavisCreator.templateEditor.saveFailed',
                    'Could not save the template image. Please try again.'
                  ),
                  { position: 'top-right' }
                );
              });
            }}
          />
        </main>
      </div>
      <ConfirmationModal
        show={showExitConfirmation}
        title={t(
          'lomavisCreator.templateEditor.exitConfirmation.title',
          'Discard template edits?'
        )}
        body={t(
          'lomavisCreator.templateEditor.exitConfirmation.body',
          'Your uploaded media and editing progress will be deleted if you close without saving.'
        )}
        primaryText={
          discarding
            ? t('lomavisCreator.templateEditor.discarding', 'Discarding…')
            : t('lomavisCreator.modal.discard')
        }
        secondaryText={t('lomavisCreator.modal.continueEditing')}
        primaryVariant="danger"
        primaryDisabled={discarding}
        secondaryDisabled={discarding}
        handleClose={() => {
          if (!discarding) setShowExitConfirmation(false);
        }}
        handleSecondary={() => setShowExitConfirmation(false)}
        handlePrimary={() => {
          discardChanges().catch(() => {
            toast.error(
              t(
                'lomavisCreator.templateEditor.discardFailed',
                'Could not discard the template media. Please try again.'
              ),
              { position: 'top-right' }
            );
          });
        }}
      />
      <OverlayTemplateSelectorModal
        show={showTemplateSelector}
        locationUuid={locationUuid}
        onClose={() => setShowTemplateSelector(false)}
        onSelect={(nextTemplate) => {
          if (hasUnsavedTemplateEdits) {
            setPendingTemplate(nextTemplate);
            setShowTemplateChangeConfirmation(true);
            return;
          }
          applyTemplateChange(nextTemplate);
        }}
      />
      <ConfirmationModal
        show={showTemplateChangeConfirmation}
        title={t(
          'lomavisCreator.templateEditor.changeConfirmation.title',
          'Change template?'
        )}
        body={t(
          'lomavisCreator.templateEditor.changeConfirmation.body',
          'Your unsaved template edits will be discarded.'
        )}
        primaryText={t(
          'lomavisCreator.templateEditor.changeConfirmation.confirm',
          'Change template'
        )}
        secondaryText={t('lomavisCreator.modal.continueEditing')}
        primaryVariant="danger"
        handleClose={() => {
          setPendingTemplate(null);
          setShowTemplateChangeConfirmation(false);
        }}
        handleSecondary={() => {
          setPendingTemplate(null);
          setShowTemplateChangeConfirmation(false);
        }}
        handlePrimary={() => {
          if (pendingTemplate) applyTemplateChange(pendingTemplate);
        }}
      />
    </ModalBody>
  );
};

export default OverlayTemplateImageEditor;
